import { handleUnauthorized, xsrfHeaders } from "./api";
import { baseUrl } from "./const";

export type AssistantMessage = { role: "user" | "assistant"; content: string };

export type AssistantRequest = {
  messages: AssistantMessage[];
  pathname: string;
  routeKey: string;
  pageTitle: string;
  lang: string;
};

export type AssistantCitation = { n: number; title: string; link: string };
export type AssistantErrorCode = "rate_limited" | "unavailable" | "disabled";

export type AssistantEvent =
  | { type: "citations"; items: AssistantCitation[] }
  | { type: "delta"; text: string }
  | { type: "done"; covered: boolean }
  | { type: "error"; code: AssistantErrorCode };

/**
 * Incremental Server-Sent-Events parser. Feed it decoded text chunks (which may split frames anywhere);
 * it calls onData with the joined `data:` payload of each complete frame.
 */
export function createSSEParser(onData: (data: string) => void) {
  let buffer = "";
  const handleFrame = (frame: string) => {
    const dataLines: string[] = [];
    for (const line of frame.split("\n")) {
      if (line.startsWith("data:")) {
        const v = line.slice(5);
        dataLines.push(v.startsWith(" ") ? v.slice(1) : v);
      }
    }
    if (dataLines.length) onData(dataLines.join("\n"));
  };
  return {
    feed(chunk: string) {
      buffer += chunk.replace(/\r\n?/g, "\n");
      let idx: number;
      while ((idx = buffer.indexOf("\n\n")) >= 0) {
        handleFrame(buffer.slice(0, idx));
        buffer = buffer.slice(idx + 2);
      }
    },
    /** process any trailing frame that wasn't terminated by a blank line */
    flush() {
      if (buffer.trim()) handleFrame(buffer);
      buffer = "";
    },
  };
}

/** Streams a chat answer. Resolves when the stream ends; rejects on network/HTTP failure or abort. */
export async function streamChat(
  req: AssistantRequest,
  onEvent: (e: AssistantEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(`${baseUrl}/assistant/chat`, {
    method: "POST",
    credentials: "same-origin",
    signal,
    headers: { "Content-Type": "application/json", Accept: "text/event-stream", ...xsrfHeaders() },
    body: JSON.stringify(req),
  });
  if (!response.ok || !response.body) {
    if (response.status === 429) return onEvent({ type: "error", code: "rate_limited" });
    if (response.status === 404) return onEvent({ type: "error", code: "disabled" });
    if (response.status === 401) {
      handleUnauthorized(response);
      throw new Error("assistant request failed: 401");
    }
    if (response.status >= 500) return onEvent({ type: "error", code: "unavailable" });
    throw new Error(`assistant request failed: ${response.status}`);
  }
  const parser = createSSEParser((data) => {
    try {
      onEvent(JSON.parse(data) as AssistantEvent);
    } catch (_e) {
      // ignore malformed frames
    }
  });
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    parser.feed(decoder.decode(value, { stream: true }));
  }
  parser.feed(decoder.decode());
  parser.flush();
}
